/**
 * Authentication Store
 *
 * Zustand store for managing authentication state and user info.
 *
 * SECURITY: JWT tokens are now stored in HttpOnly cookies (not localStorage).
 * This prevents XSS attacks from stealing tokens via JavaScript.
 *
 * The store only tracks:
 * - User information (returned by login endpoint)
 * - Authentication state (isAuthenticated)
 * - Loading/error states
 *
 * Tokens are managed by the browser's cookie storage and sent automatically
 * with all API requests when credentials: 'include' is set.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from '@shared/services/apiClient';

interface User {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  role?: string;
  is_active?: boolean;
}

interface AuthState {
  // State (NO tokens - they are in HttpOnly cookies)
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  expiresIn: number | null; // Seconds until access token expires

  // Actions
  login: (username: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  clearError: () => void;
}

/**
 * Initial state
 */
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  expiresIn: null,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Login with username and password
       *
       * SECURITY: Tokens are set as HttpOnly cookies by the server.
       * We only store user info and authentication state locally.
       *
       * @param username - User's username
       * @param password - User's password
       * @param rememberMe - If true, server sets longer-lived refresh token (default: true)
       */
      login: async (username: string, password: string, rememberMe = true) => {
        set({ isLoading: true, error: null });

        try {
          // credentials: 'include' is set in apiClient to receive cookies
          // Pass rememberMe to backend so it can set appropriate cookie expiry
          const response = await apiClient.post('/auth/login', {
            username,
            password,
            remember_me: rememberMe,
          });
          const { success, expires_in, user } = response.data;

          if (!success) {
            throw new Error('Login failed');
          }

          // Store user info (tokens are in HttpOnly cookies, NOT accessible to JS)
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            expiresIn: expires_in,
          });

          console.log(`[Auth] Login successful for user: ${user.username} (token in HttpOnly cookie)`);
        } catch (error: unknown) {
          const axiosError = error as { response?: { data?: { detail?: string } } };
          const errorMessage = axiosError.response?.data?.detail || 'Login failed. Please try again.';

          set({
            isLoading: false,
            error: errorMessage,
          });

          console.error('[Auth] Login error:', errorMessage);
          throw error;
        }
      },

      /**
       * Logout and clear authentication state
       *
       * SECURITY: Calls backend to clear HttpOnly cookies and blacklist tokens.
       */
      logout: async () => {
        try {
          // Call logout endpoint to clear cookies on server
          await apiClient.post('/auth/logout');
          console.log('[Auth] Logout successful (cookies cleared by server)');
        } catch (error) {
          // Even if logout fails, clear local state
          console.error('[Auth] Logout API call failed:', error);
        }

        // Clear local state
        set(initialState);
        console.log('[Auth] Local state cleared');
      },

      /**
       * Refresh access token
       *
       * SECURITY: The refresh token is in an HttpOnly cookie.
       * This just triggers the refresh endpoint which updates the access_token cookie.
       */
      refreshAccessToken: async () => {
        try {
          // The refresh token cookie is sent automatically
          const response = await apiClient.post('/auth/refresh');
          const { success, expires_in } = response.data;

          if (!success) {
            throw new Error('Token refresh failed');
          }

          // Update expiry time (new access_token cookie is set by server)
          set({ expiresIn: expires_in });

          console.log('[Auth] Access token refreshed successfully (new cookie set by server)');
        } catch (error: unknown) {
          console.error('[Auth] Token refresh failed:', error);

          // DON'T logout immediately - let apiClient's circuit breaker handle it
          // The circuit breaker will dispatch 'auth:session-expired' after 3 consecutive failures
          // This prevents premature logout on transient network issues or race conditions
          throw error;
        }
      },

      /**
       * Check if user is still authenticated
       *
       * Validates the current session by calling /auth/me endpoint.
       * Returns true if authenticated, false otherwise.
       */
      checkAuth: async () => {
        try {
          const response = await apiClient.get('/auth/me');
          const user = response.data;

          set({
            user,
            isAuthenticated: true,
          });

          console.log(`[Auth] Session valid for user: ${user.username}`);
          return true;
        } catch (error) {
          console.log('[Auth] Session check failed - not authenticated');
          set(initialState);
          return false;
        }
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'dms-auth-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist user info and auth state (NOT tokens - they are in HttpOnly cookies)
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
